import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, Loader2, ShieldAlert } from 'lucide-react';

import {
  useGetRbacCatalogueQuery,
  useGetRbacMatrixQuery,
  useUpdateRbacMatrixMutation,
} from '@/redux/services/admin/rbac';
import { useToast } from '@/hooks/use-toast';
import styles from './RolesAccessPanel.module.css';

/**
 * Roles & Access — what each role in this organization may do.
 *
 * Everything on screen comes from the server. The permission list, its labels
 * and its grouping are `GET /rbac/catalogue`; the current answers are
 * `GET /rbac/matrix`, already resolved against this organization's overrides.
 * Nothing is listed in this file, deliberately: a copy of the policy here would
 * drift the first time a permission was added on the backend, and the failure
 * would be a screen that quietly omits a toggle.
 *
 * Changes are batched. The grid invites several flips before a save, and one
 * request means one audit moment rather than six interleaved with somebody
 * else's.
 *
 * Saving writes only what changed. Sending the whole grid would rewrite rows
 * nobody touched and fill the audit trail with noise.
 */

const ROLE_LABEL = {
  client_admin: 'Client Admin',
  branch_admin: 'Branch Admin',
  loan_officer_admin: 'Loan Officer Admin',
  underwriter: 'Underwriter',
  loan_officer: 'Loan Officer',
  loan_processor: 'Loan Processor',
  closer: 'Closer',
  auditor: 'Auditor',
  read_only: 'Read Only',
};

const ROLE_DOT = {
  client_admin: '#3c588b',
  branch_admin: '#2563eb',
  loan_officer_admin: '#4a6aa3',
  underwriter: '#6d48e5',
  loan_officer: '#0e9c8b',
  loan_processor: '#c2691d',
  closer: '#4a3aa7',
  auditor: '#64748b',
  read_only: '#94a3b8',
};

const label = (role) => ROLE_LABEL[role] || role;

const RolesAccessPanel = () => {
  const { toast } = useToast();
  const catalogue = useGetRbacCatalogueQuery();
  const matrix = useGetRbacMatrixQuery();
  const [save, { isLoading: isSaving }] = useUpdateRbacMatrixMutation();

  const [role, setRole] = useState(null);
  /** Pending flips, keyed `role|permission`. Only these are sent. */
  const [pending, setPending] = useState({});
  /** Which permission group is expanded. One at a time; null means all shut. */
  const [openSection, setOpenSection] = useState(null);

  const permissions = catalogue.data?.permissions ?? [];
  const roles = catalogue.data?.roles ?? [];
  const held = matrix.data?.matrix ?? {};

  const activeRole = role || roles[0];

  const sections = useMemo(() => {
    const grouped = [];
    permissions.forEach((perm) => {
      let bucket = grouped.find((g) => g.name === perm.section);
      if (!bucket) {
        bucket = { name: perm.section, items: [] };
        grouped.push(bucket);
      }
      bucket.items.push(perm);
    });
    return grouped;
  }, [permissions]);

  /** Stored answer, with any unsaved flip laid over it. */
  const isOn = (permKey) => {
    const pendingKey = `${activeRole}|${permKey}`;
    if (pendingKey in pending) return pending[pendingKey];
    return (held[permKey] ?? []).includes(activeRole);
  };

  const toggle = (permKey) => {
    const pendingKey = `${activeRole}|${permKey}`;
    const stored = (held[permKey] ?? []).includes(activeRole);
    const next = !isOn(permKey);

    setPending((prev) => {
      const copy = { ...prev };
      // Flipping back to the stored value is not a change. Dropping it keeps
      // the count honest and keeps no-op rows out of the audit trail.
      if (next === stored) {
        delete copy[pendingKey];
      } else {
        copy[pendingKey] = next;
      }
      return copy;
    });
  };

  const changeCount = Object.keys(pending).length;

  const onSave = async () => {
    const entries = Object.entries(pending).map(([key, allowed]) => {
      const [entryRole, permKey] = key.split('|');
      const lastDot = permKey.lastIndexOf('.');
      return {
        role: entryRole,
        resource: permKey.slice(0, lastDot),
        action: permKey.slice(lastDot + 1),
        allowed,
      };
    });

    try {
      const result = await save({ entries }).unwrap();
      setPending({});
      const orphaned = result?.unheld?.length ?? 0;
      toast({
        title: `Saved ${entries.length} change${entries.length === 1 ? '' : 's'}`,
        description: orphaned
          ? `${orphaned} permission${orphaned === 1 ? '' : 's'} now held by nobody.`
          : 'Applies immediately, to everyone with these roles.',
      });
    } catch (error) {
      // The server refuses some edits on purpose — removing the permission
      // that edits permissions, granting a platform role. Its wording explains
      // why, so it is shown rather than replaced.
      toast({
        title: 'Could not save',
        description:
          error?.data?.detail || 'The change was refused. Nothing was applied.',
        variant: 'destructive',
      });
    }
  };

  if (catalogue.isLoading || matrix.isLoading) {
    return (
      <div className={styles.state}>
        <Loader2 className={styles.spin} size={16} aria-hidden="true" />
        Loading access settings…
      </div>
    );
  }

  /* 403 is the expected answer for anyone who is not a client admin, so it
     reads as a boundary rather than a failure. */
  const status = catalogue.error?.status ?? matrix.error?.status;
  if (status === 403) {
    return (
      <div className={styles.state}>
        <ShieldAlert size={16} aria-hidden="true" />
        Only a client admin can change what each role may do.
      </div>
    );
  }
  if (catalogue.isError || matrix.isError) {
    return (
      <div className={styles.state}>
        <AlertTriangle size={16} aria-hidden="true" />
        Access settings could not be loaded.
      </div>
    );
  }

  const unheld = matrix.data?.unheld ?? [];
  const dutyWarnings = matrix.data?.dutyWarnings ?? [];

  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <h3 className={styles.title}>Roles &amp; Access</h3>
        <p className={styles.subtitle}>
          Pick a role, then choose what it can do. Changes apply to everyone with
          that role in your organization, as soon as you save.
        </p>
      </div>

      <div className={styles.rolePick} role="tablist" aria-label="Role">
        {roles.map((entry) => {
          const selected = entry === activeRole;
          const dirty = Object.keys(pending).some((k) => k.startsWith(`${entry}|`));
          return (
            <button
              key={entry}
              type="button"
              role="tab"
              id={`rbac-role-${entry}`}
              aria-selected={selected}
              aria-controls="rbac-permissions"
              tabIndex={selected ? 0 : -1}
              className={`${styles.roleBtn} ${selected ? styles.roleBtnOn : ''}`}
              onClick={() => setRole(entry)}
            >
              <span
                className={styles.roleDot}
                style={{ background: ROLE_DOT[entry] || '#94a3b8' }}
                aria-hidden="true"
              />
              {label(entry)}
              {/* Unsaved work on a role you have navigated away from is
                  otherwise invisible. */}
              {dirty ? <span className={styles.dirtyMark} aria-label="unsaved changes">•</span> : null}
            </button>
          );
        })}
      </div>

      {/* Closed by default. There is one line per role-and-rule pair, so a
          dozen roles produce a wall of amber that pushes the permission list —
          the thing you came to change — off the screen. The count is the part
          you need at a glance; the wording is there when you open it. */}
      {dutyWarnings.length ? (
        <details className={styles.warn}>
          <summary className={styles.warnSummary}>
            <AlertTriangle size={14} aria-hidden="true" />
            <span>
              {dutyWarnings.length} separation-of-duties{' '}
              {dutyWarnings.length === 1 ? 'warning' : 'warnings'}
            </span>
            <ChevronDown size={14} className={styles.warnChevron} aria-hidden="true" />
          </summary>
          <div className={styles.warnBody}>
            {dutyWarnings.map((w) => (
              <p key={`${w.role}-${w.holds}`}>
                <b>{label(w.role)}</b> both prepares and approves. {w.why}
              </p>
            ))}
          </div>
        </details>
      ) : null}

      <div
        className={styles.list}
        id="rbac-permissions"
        role="tabpanel"
        aria-labelledby={`rbac-role-${activeRole}`}
      >
        {/* One group open at a time. The full matrix is ~60 rows across a
            dozen groups; expanded it is several screens of scrolling to reach
            a permission you already know the name of. Any group holding an
            unsaved change opens with it, so a pending flip is never hidden. */}
        {sections.map((section) => {
          const onCount = section.items.filter((perm) => isOn(perm.key)).length;
          const hasPending = section.items.some(
            (perm) => `${activeRole}|${perm.key}` in pending
          );
          return (
          <details
            key={section.name}
            className={styles.section}
            open={hasPending || openSection === section.name}
            onToggle={(event) => {
              if (event.target.open) setOpenSection(section.name);
              else if (openSection === section.name) setOpenSection(null);
            }}
          >
            <summary className={styles.sectionName}>
              <ChevronDown size={13} className={styles.sectionChevron} aria-hidden="true" />
              <span className={styles.sectionNameText}>{section.name}</span>
              <span className={styles.sectionCount}>
                {onCount}/{section.items.length}
                {hasPending ? <span className={styles.dirtyMark} aria-label="unsaved changes">•</span> : null}
              </span>
            </summary>
            {section.items.map((perm) => {
              const on = isOn(perm.key);
              const changed = `${activeRole}|${perm.key}` in pending;
              const orphaned = unheld.includes(perm.key);
              return (
                <div
                  key={perm.key}
                  className={`${styles.row} ${changed ? styles.rowChanged : ''}`}
                >
                  <span className={styles.rowText}>
                    <b>{perm.label}</b>
                    <code>{perm.key}</code>
                  </span>
                  {orphaned && !on ? (
                    <span className={styles.orphan} title="No role can do this">
                      nobody
                    </span>
                  ) : null}
                  {/* A switch, not a checkbox: it takes effect on save but it
                      describes a state rather than a selection. */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={`${perm.label} for ${label(activeRole)}`}
                    className={`${styles.toggle} ${on ? styles.toggleOn : ''}`}
                    onClick={() => toggle(perm.key)}
                  >
                    <span className={styles.knob} />
                  </button>
                </div>
              );
            })}
          </details>
          );
        })}
      </div>

      <div className={styles.foot}>
        <p className={styles.note}>
          {changeCount
            ? `${changeCount} unsaved change${changeCount === 1 ? '' : 's'}.`
            : 'The server enforces these. Hiding a button is a courtesy; the API refuses the call either way.'}
        </p>
        {changeCount ? (
          <button
            type="button"
            className={styles.ghostBtn}
            onClick={() => setPending({})}
            disabled={isSaving}
          >
            Discard
          </button>
        ) : null}
        <button
          type="button"
          className={styles.saveBtn}
          disabled={!changeCount || isSaving}
          onClick={onSave}
        >
          {isSaving ? 'Saving…' : 'Save access'}
        </button>
      </div>
    </div>
  );
};

export default RolesAccessPanel;
